'use client';

import { useRef } from 'react';
import Image from 'next/image';
import { format } from 'date-fns';
import { useLocale, useTranslations } from 'next-intl';
import { useForm, useFieldArray, FieldErrors } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useMutation } from '@tanstack/react-query';
import { FileIcon, PlusIcon, RefreshCw, SendIcon, Trash2Icon, UploadIcon, XIcon } from 'lucide-react';
import { toast } from 'sonner';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { DatePicker } from '@/components/ui/date-picker';
import { PhoneInput } from '@/components/ui/phone-input';
import { MonthPicker } from '@/components/ui/month-picker';
import { useCaptcha } from '@/hooks';
import { submitJobApplication } from '@/lib/api';
import { getErrorMessage, setValidationErrors } from '@/lib/utils';
const GENDERS = ['MALE', 'FEMALE', 'DIVERSE'] as const;
const MARITAL_STATUSES = ['SINGLE', 'MARRIED'] as const;
const EDUCATION_LEVELS = ['PRIMARY_SCHOOL', 'MIDDLE_SCHOOL', 'HIGH_SCHOOL', 'VOCATIONAL_HIGH_SCHOOL', 'ASSOCIATE', 'BACHELOR', 'MASTER', 'DOCTORATE', 'OTHER'] as const;
const GRADUATION_STATUSES = ['GRADUATED', 'ONGOING', 'DROPPED_OUT'] as const;
const LANGUAGE_PROFICIENCY_LEVELS = ['BEGINNER', 'ELEMENTARY', 'INTERMEDIATE', 'UPPER_INTERMEDIATE', 'ADVANCED', 'NATIVE'] as const;
const SKILL_LEVELS = ['BEGINNER', 'INTERMEDIATE', 'ADVANCED', 'EXPERT'] as const;
const YYYY_MM_REGEX = /^\d{4}-(0[1-9]|1[0-2])$/;
const YYYY_MM_DD_REGEX = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;
type PropsType = {
  captcha?: boolean;
};
const JobApplicationForm = ({
  captcha
}: PropsType) => {
  const t = useTranslations();
  const locale = useLocale();
  const photoInputRef = useRef<HTMLInputElement | null>(null);
  const filesInputRef = useRef<HTMLInputElement | null>(null);
  const {
    data: captchaData,
    isLoading: isLoadingCaptcha,
    refetch: refreshCaptcha
  } = useCaptcha({
    size: 'medium'
  }, !!captcha);
  const requiredEnum = <TValues extends readonly [string, ...string[]],>(values: TValues) => z.enum(values).optional().refine(value => !!value, t('required'));
  const optionalText = (max: number) => z.string().trim().max(max, t('max_value', {
    value: max
  }));
  const optionalMonth = (max_0: number = 20) => z.string().trim().max(max_0, t('max_value', {
    value: max_0
  })).refine(value_0 => !value_0 || YYYY_MM_REGEX.test(value_0), t('hint_ym_format'));
  const optionalDate = () => z.string().trim().max(10, t('max_value', {
    value: 10
  })).refine(value_1 => !value_1 || YYYY_MM_DD_REGEX.test(value_1), t('hint_ymd_format'));
  const formSchema = z.object({
    firstname: z.string().trim().min(1, t('required')).max(100, t('max_value', {
      value: 100
    })),
    lastname: z.string().trim().min(1, t('required')).max(100, t('max_value', {
      value: 100
    })),
    birthPlace: z.string().trim().min(1, t('required')).max(255, t('max_value', {
      value: 255
    })),
    birthDate: z.string().trim().min(1, t('required')).max(10, t('max_value', {
      value: 10
    })).regex(YYYY_MM_DD_REGEX, t('hint_ymd_format')),
    email: z.string().trim().min(1, t('required')).check(z.email({
      error: t('invalid_email')
    })),
    phone: z.string().trim().min(1, t('required')).max(50, t('max_value', {
      value: 50
    })),
    address: z.string().trim().min(1, t('required')),
    gender: requiredEnum(GENDERS),
    maritalStatus: requiredEnum(MARITAL_STATUSES),
    driverLicense: z.boolean(),
    driverLicenseClass: optionalText(50),
    healthProblems: z.boolean(),
    healthProblemsDescription: optionalText(1000),
    travelRestrictions: z.boolean(),
    travelRestrictionsDescription: optionalText(1000),
    education: z.array(z.object({
      level: requiredEnum(EDUCATION_LEVELS),
      schoolName: z.string().trim().min(1, t('required')).max(255, t('max_value', {
        value: 255
      })),
      department: optionalText(255),
      startDate: z.string().trim().min(1, t('required')).max(20, t('max_value', {
        value: 20
      })).regex(YYYY_MM_REGEX, t('hint_ym_format')),
      endDate: optionalMonth(),
      graduationStatus: requiredEnum(GRADUATION_STATUSES)
    })).min(1, t('required')),
    foreignLanguages: z.array(z.object({
      language: z.string().trim().min(1, t('required')).max(100, t('max_value', {
        value: 100
      })),
      listening: requiredEnum(LANGUAGE_PROFICIENCY_LEVELS),
      speaking: requiredEnum(LANGUAGE_PROFICIENCY_LEVELS),
      reading: requiredEnum(LANGUAGE_PROFICIENCY_LEVELS),
      writing: requiredEnum(LANGUAGE_PROFICIENCY_LEVELS)
    })),
    workExperience: z.array(z.object({
      companyName: z.string().trim().min(1, t('required')).max(255, t('max_value', {
        value: 255
      })),
      position: z.string().trim().min(1, t('required')).max(255, t('max_value', {
        value: 255
      })),
      startDate: z.string().trim().min(1, t('required')).max(20, t('max_value', {
        value: 20
      })).regex(YYYY_MM_REGEX, t('hint_ym_format')),
      endDate: optionalMonth(),
      description: optionalText(2000),
      currentlyWorking: z.boolean().optional()
    })),
    skills: z.array(z.object({
      skillName: z.string().trim().min(1, t('required')).max(100, t('max_value', {
        value: 100
      })),
      level: requiredEnum(SKILL_LEVELS)
    })),
    certifications: z.array(z.object({
      certificationName: z.string().trim().min(1, t('required')).max(255, t('max_value', {
        value: 255
      })),
      issuingOrganization: z.string().trim().min(1, t('required')).max(255, t('max_value', {
        value: 255
      })),
      certificationDate: optionalMonth(),
      validityPeriod: optionalText(100)
    })),
    references: z.array(z.object({
      name: z.string().trim().min(1, t('required')).max(255, t('max_value', {
        value: 255
      })),
      position: optionalText(255),
      phone: optionalText(50),
      email: z.string().trim().max(255, t('max_value', {
        value: 255
      })).refine(value_2 => !value_2 || z.email().safeParse(value_2).success, t('invalid_email'))
    })),
    photo: z.custom<File | undefined>(value_3 => value_3 === undefined || value_3 instanceof File, t('invalid')).optional(),
    files: z.array(z.custom<File>(value_4 => value_4 instanceof File, t('invalid'))).optional(),
    position: optionalText(255),
    availableFrom: optionalDate(),
    additionalInformation: optionalText(5000),
    captchaText: captcha ? z.string().trim().min(1, t('required')) : z.string().optional()
  }).superRefine((data, ctx) => {
    if (data.driverLicense && !data.driverLicenseClass?.trim()) {
      ctx.addIssue({
        code: 'custom',
        path: ['driverLicenseClass'],
        message: t('required')
      });
    }
    if (data.healthProblems && !data.healthProblemsDescription?.trim()) {
      ctx.addIssue({
        code: 'custom',
        path: ['healthProblemsDescription'],
        message: t('required')
      });
    }
    if (data.travelRestrictions && !data.travelRestrictionsDescription?.trim()) {
      ctx.addIssue({
        code: 'custom',
        path: ['travelRestrictionsDescription'],
        message: t('required')
      });
    }
    data.workExperience.forEach((item, index) => {
      if (!item.currentlyWorking && !item.endDate?.trim()) {
        ctx.addIssue({
          code: 'custom',
          path: ['workExperience', index, 'endDate'],
          message: t('required')
        });
      }
    });
  });
  type FormValues = z.infer<typeof formSchema>;
  const createEducationItem = (): FormValues['education'][number] => ({
    level: undefined,
    schoolName: '',
    department: '',
    startDate: '',
    endDate: '',
    graduationStatus: undefined
  });
  const defaultValues: FormValues = {
    firstname: '',
    lastname: '',
    birthPlace: '',
    birthDate: '',
    email: '',
    phone: '',
    address: '',
    gender: undefined,
    maritalStatus: undefined,
    driverLicense: false,
    driverLicenseClass: '',
    healthProblems: false,
    healthProblemsDescription: '',
    travelRestrictions: false,
    travelRestrictionsDescription: '',
    education: [],
    foreignLanguages: [],
    workExperience: [],
    skills: [],
    certifications: [],
    references: [],
    photo: undefined,
    files: [],
    position: '',
    availableFrom: '',
    additionalInformation: '',
    captchaText: ''
  };
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues
  });
  const scrollToFirstError = (errors: FieldErrors<FormValues>) => {
    const getFirstErrorPath = (obj: object, prefix = ''): string | null => {
      for (const [key, value_5] of Object.entries(obj)) {
        const path = prefix ? `${prefix}.${key}` : key;
        if (value_5 && typeof value_5 === 'object') {
          if ('message' in value_5 || 'type' in value_5) {
            return path;
          }
          const nested = getFirstErrorPath(value_5 as object, path);
          if (nested) {
            return nested;
          }
        }
      }
      return null;
    };
    const firstPath = getFirstErrorPath(errors);
    if (!firstPath) {
      return;
    }
    const el = document.querySelector(`[name="${firstPath}"], [id="${firstPath}"], [data-field="${firstPath}"]`) ?? document.querySelector(`#${CSS.escape(firstPath)}`);
    if (el) {
      el.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
      (el as HTMLElement).focus?.();
    }
  };
  const educationFields = useFieldArray({
    control: form.control,
    name: 'education'
  });
  const languageFields = useFieldArray({
    control: form.control,
    name: 'foreignLanguages'
  });
  const workExperienceFields = useFieldArray({
    control: form.control,
    name: 'workExperience'
  });
  const skillFields = useFieldArray({
    control: form.control,
    name: 'skills'
  });
  const certificationFields = useFieldArray({
    control: form.control,
    name: 'certifications'
  });
  const referenceFields = useFieldArray({
    control: form.control,
    name: 'references'
  });
  const driverLicense = form.watch('driverLicense');
  const healthProblems = form.watch('healthProblems');
  const travelRestrictions = form.watch('travelRestrictions');
  const selectedPhoto = form.watch('photo');
  const selectedFiles = form.watch('files') ?? [];
  const clearFileInputs = () => {
    if (photoInputRef.current) {
      photoInputRef.current.value = '';
    }
    if (filesInputRef.current) {
      filesInputRef.current.value = '';
    }
  };
  const appendIfFilled = (fd: FormData, key_0: string, value_6?: string | null) => {
    if (value_6 && value_6.trim()) {
      fd.append(key_0, value_6.trim());
    }
  };
  const {
    mutate,
    isPending
  } = useMutation({
    mutationFn: (payload: FormData) => submitJobApplication(payload),
    onSuccess: async () => {
      toast.success(t('application_submitted_success'));
      form.reset(defaultValues);
      clearFileInputs();
      if (captcha) {
        await refreshCaptcha();
      }
    },
    onError: async (error: any) => {
      if (error.response?.status === 422) {
        const items = error?.response?.data?.items || {};
        const remapped = Object.fromEntries(Object.entries(items).map(([key_1, value_7]) => [key_1 === 'captcha' ? 'captchaText' : key_1, value_7]));
        setValidationErrors({
          response: {
            data: {
              items: remapped
            }
          }
        }, form.setError);
      } else {
        toast.error(getErrorMessage(error, t('application_submitted_error')));
      }
      if (captcha) {
        await refreshCaptcha();
      }
    }
  });
  const onSubmit = (data_0: FormValues) => {
    if (captcha && !captchaData) {
      toast.error(t('application_submitted_error'));
      return;
    }
    const fd_0 = new FormData();
    fd_0.append('firstname', data_0.firstname.trim());
    fd_0.append('lastname', data_0.lastname.trim());
    fd_0.append('birthPlace', data_0.birthPlace.trim());
    fd_0.append('birthDate', data_0.birthDate.trim());
    fd_0.append('email', data_0.email.trim());
    fd_0.append('phone', data_0.phone.trim());
    fd_0.append('address', data_0.address.trim());
    fd_0.append('driverLicense', String(data_0.driverLicense));
    fd_0.append('healthProblems', String(data_0.healthProblems));
    fd_0.append('travelRestrictions', String(data_0.travelRestrictions));
    appendIfFilled(fd_0, 'gender', data_0.gender);
    appendIfFilled(fd_0, 'maritalStatus', data_0.maritalStatus);
    appendIfFilled(fd_0, 'driverLicenseClass', data_0.driverLicenseClass);
    appendIfFilled(fd_0, 'healthProblemsDescription', data_0.healthProblemsDescription);
    appendIfFilled(fd_0, 'travelRestrictionsDescription', data_0.travelRestrictionsDescription);
    appendIfFilled(fd_0, 'position', data_0.position);
    appendIfFilled(fd_0, 'availableFrom', data_0.availableFrom);
    appendIfFilled(fd_0, 'additionalInformation', data_0.additionalInformation);
    data_0.education.forEach((item_0, index_0) => {
      appendIfFilled(fd_0, `education[${index_0}].level`, item_0.level);
      fd_0.append(`education[${index_0}].schoolName`, item_0.schoolName.trim());
      appendIfFilled(fd_0, `education[${index_0}].department`, item_0.department);
      fd_0.append(`education[${index_0}].startDate`, item_0.startDate.trim());
      appendIfFilled(fd_0, `education[${index_0}].endDate`, item_0.endDate);
      appendIfFilled(fd_0, `education[${index_0}].graduationStatus`, item_0.graduationStatus);
    });
    data_0.foreignLanguages.forEach((item_1, index_1) => {
      fd_0.append(`foreignLanguages[${index_1}].language`, item_1.language.trim());
      appendIfFilled(fd_0, `foreignLanguages[${index_1}].listening`, item_1.listening);
      appendIfFilled(fd_0, `foreignLanguages[${index_1}].speaking`, item_1.speaking);
      appendIfFilled(fd_0, `foreignLanguages[${index_1}].reading`, item_1.reading);
      appendIfFilled(fd_0, `foreignLanguages[${index_1}].writing`, item_1.writing);
    });
    data_0.workExperience.forEach((item_2, index_2) => {
      fd_0.append(`workExperience[${index_2}].companyName`, item_2.companyName.trim());
      fd_0.append(`workExperience[${index_2}].position`, item_2.position.trim());
      fd_0.append(`workExperience[${index_2}].startDate`, item_2.startDate.trim());
      appendIfFilled(fd_0, `workExperience[${index_2}].endDate`, item_2.endDate);
      appendIfFilled(fd_0, `workExperience[${index_2}].description`, item_2.description);
    });
    data_0.skills.forEach((item_3, index_3) => {
      fd_0.append(`skills[${index_3}].skillName`, item_3.skillName.trim());
      appendIfFilled(fd_0, `skills[${index_3}].level`, item_3.level);
    });
    data_0.certifications.forEach((item_4, index_4) => {
      fd_0.append(`certifications[${index_4}].certificationName`, item_4.certificationName.trim());
      fd_0.append(`certifications[${index_4}].issuingOrganization`, item_4.issuingOrganization.trim());
      appendIfFilled(fd_0, `certifications[${index_4}].certificationDate`, item_4.certificationDate);
      appendIfFilled(fd_0, `certifications[${index_4}].validityPeriod`, item_4.validityPeriod);
    });
    data_0.references.forEach((item_5, index_5) => {
      fd_0.append(`references[${index_5}].name`, item_5.name.trim());
      appendIfFilled(fd_0, `references[${index_5}].position`, item_5.position);
      appendIfFilled(fd_0, `references[${index_5}].phone`, item_5.phone);
      appendIfFilled(fd_0, `references[${index_5}].email`, item_5.email);
    });
    if (data_0.photo) {
      fd_0.append('photo', data_0.photo);
    }
    data_0.files?.forEach(file => {
      fd_0.append('files', file);
    });
    if (captcha && captchaData) {
      fd_0.append('captcha.key', captchaData.key);
      fd_0.append('captcha.text', data_0.captchaText ?? '');
    }
    mutate(fd_0);
  };
  const genderOptions = [{
    value: 'MALE',
    label: t('male')
  }, {
    value: 'FEMALE',
    label: t('female')
  }, {
    value: 'DIVERSE',
    label: t('diverse')
  }];
  const maritalStatusOptions = [{
    value: 'SINGLE',
    label: t('single')
  }, {
    value: 'MARRIED',
    label: t('married')
  }];
  const educationLevelOptions = [{
    value: 'PRIMARY_SCHOOL',
    label: t('primary_school')
  }, {
    value: 'MIDDLE_SCHOOL',
    label: t('middle_school')
  }, {
    value: 'HIGH_SCHOOL',
    label: t('high_school')
  }, {
    value: 'VOCATIONAL_HIGH_SCHOOL',
    label: t('vocational_high_school')
  }, {
    value: 'ASSOCIATE',
    label: t('associate_degree')
  }, {
    value: 'BACHELOR',
    label: t('bachelor_degree')
  }, {
    value: 'MASTER',
    label: t('master_degree')
  }, {
    value: 'DOCTORATE',
    label: t('doctorate_degree')
  }, {
    value: 'OTHER',
    label: t('other')
  }];
  const graduationStatusOptions = [{
    value: 'GRADUATED',
    label: t('graduated')
  }, {
    value: 'ONGOING',
    label: t('ongoing')
  }, {
    value: 'DROPPED_OUT',
    label: t('dropped_out')
  }];
  const languageProficiencyOptions = [{
    value: 'BEGINNER',
    label: t('beginner')
  }, {
    value: 'ELEMENTARY',
    label: t('elementary')
  }, {
    value: 'INTERMEDIATE',
    label: t('intermediate')
  }, {
    value: 'UPPER_INTERMEDIATE',
    label: t('upper_intermediate')
  }, {
    value: 'ADVANCED',
    label: t('advanced')
  }, {
    value: 'NATIVE',
    label: t('native_language')
  }];
  const skillLevelOptions = [{
    value: 'BEGINNER',
    label: t('beginner')
  }, {
    value: 'INTERMEDIATE',
    label: t('intermediate')
  }, {
    value: 'ADVANCED',
    label: t('advanced')
  }, {
    value: 'EXPERT',
    label: t('expert')
  }];
  return <div className="space-y-6">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit, scrollToFirstError)} className="space-y-6">
          <div className="bg-card space-y-6 rounded-lg border p-6 shadow-xs">
            <h3 className="text-xl font-semibold">{t('personal_information')}</h3>
            <div className="grid items-start gap-6 md:grid-cols-2 lg:grid-cols-3">
              <FormField control={form.control} name="firstname" render={({
              field
            }) => <FormItem>
                    <FormLabel required>{t('firstname')}</FormLabel>
                    <FormControl>
                      <Input {...field} disabled={isPending} placeholder={t('firstname')} className="h-9" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
              <FormField control={form.control} name="lastname" render={({
              field: field_0
            }) => <FormItem>
                    <FormLabel required>{t('lastname')}</FormLabel>
                    <FormControl>
                      <Input {...field_0} disabled={isPending} placeholder={t('lastname')} className="h-9" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
              <FormField control={form.control} name="birthPlace" render={({
              field: field_1
            }) => <FormItem>
                    <FormLabel required>{t('birth_place')}</FormLabel>
                    <FormControl>
                      <Input {...field_1} disabled={isPending} placeholder={t('birth_place')} className="h-9" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
              <FormField control={form.control} name="birthDate" render={({
              field: field_2
            }) => <FormItem>
                    <FormLabel required>{t('birth_date')}</FormLabel>
                    <FormControl>
                      <DatePicker locale={locale} value={field_2.value ? new Date(`${field_2.value}T12:00:00`) : undefined} onChange={date => field_2.onChange(date ? format(date, 'yyyy-MM-dd') : '')} placeholder={t('birth_date')} isInvalid={!!form.formState.errors.birthDate} disabled={isPending} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
              <FormField control={form.control} name="email" render={({
              field: field_3
            }) => <FormItem>
                    <FormLabel required>{t('email')}</FormLabel>
                    <FormControl>
                      <Input {...field_3} type="email" disabled={isPending} placeholder={t('email')} className="h-9" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
              <FormField control={form.control} name="phone" render={({
              field: field_4
            }) => <FormItem>
                    <FormLabel required>{t('phone')}</FormLabel>
                    <FormControl>
                      <PhoneInput international value={field_4.value} onChange={field_4.onChange} disabled={isPending} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
              <FormField control={form.control} name="gender" render={({
              field: field_5
            }) => <FormItem>
                    <FormLabel required>{t('gender')}</FormLabel>
                    <Select value={field_5.value ?? ''} onValueChange={field_5.onChange} disabled={isPending}>
                      <FormControl>
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder={t('select')} />
                          {field_5.value && <span role="button" aria-label={t('clear')} className="text-muted-foreground hover:text-foreground ml-auto shrink-0" onPointerDown={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      field_5.onChange(undefined);
                    }}>
                              <XIcon className="h-3.5 w-3.5" />
                            </span>}
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {genderOptions.map(option => <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>)}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>} />
              <FormField control={form.control} name="maritalStatus" render={({
              field: field_6
            }) => <FormItem>
                    <FormLabel required>{t('marital_status')}</FormLabel>
                    <Select value={field_6.value ?? ''} onValueChange={field_6.onChange} disabled={isPending}>
                      <FormControl>
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder={t('select')} />
                          {field_6.value && <span role="button" aria-label={t('clear')} className="text-muted-foreground hover:text-foreground ml-auto shrink-0" onPointerDown={e_0 => {
                      e_0.preventDefault();
                      e_0.stopPropagation();
                      field_6.onChange(undefined);
                    }}>
                              <XIcon className="h-3.5 w-3.5" />
                            </span>}
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {maritalStatusOptions.map(option_0 => <SelectItem key={option_0.value} value={option_0.value}>
                            {option_0.label}
                          </SelectItem>)}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>} />
              <FormField control={form.control} name="address" render={({
              field: field_7
            }) => <FormItem className="md:col-span-2 lg:col-span-3">
                    <FormLabel required>{t('address')}</FormLabel>
                    <FormControl>
                      <Textarea {...field_7} disabled={isPending} placeholder={t('address')} className="h-32" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
            </div>
          </div>

          <div className="bg-card space-y-6 rounded-lg border p-6 shadow-xs">
            <h3 className="text-xl font-semibold">{t('additional_info')}</h3>
            <div className="grid grid-cols-1 items-start gap-4 md:grid-cols-3">
              {/* Driver License column */}
              <div className="space-y-3">
                <FormField control={form.control} name="driverLicense" render={({
                field: field_8
              }) => <FormItem className="flex flex-row items-center justify-between gap-4 rounded-lg border p-4">
                      <FormLabel>{t('driver_license')}</FormLabel>
                      <FormControl>
                        <Switch checked={field_8.value} onCheckedChange={field_8.onChange} disabled={isPending} />
                      </FormControl>
                    </FormItem>} />
                {driverLicense && <FormField control={form.control} name="driverLicenseClass" render={({
                field: field_9
              }) => <FormItem>
                        <FormLabel required>{t('driver_license_class')}</FormLabel>
                        <FormControl>
                          <Input {...field_9} autoFocus disabled={isPending} placeholder={t('driver_license_class')} className="h-9" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>} />}
              </div>

              {/* Health Problems column */}
              <div className="space-y-3">
                <FormField control={form.control} name="healthProblems" render={({
                field: field_10
              }) => <FormItem className="flex flex-row items-center justify-between gap-4 rounded-lg border p-4">
                      <FormLabel>{t('health_problems')}</FormLabel>
                      <FormControl>
                        <Switch checked={field_10.value} onCheckedChange={field_10.onChange} disabled={isPending} />
                      </FormControl>
                    </FormItem>} />
                {healthProblems && <FormField control={form.control} name="healthProblemsDescription" render={({
                field: field_11
              }) => <FormItem>
                        <FormLabel required>{t('health_problems_description')}</FormLabel>
                        <FormControl>
                          <Textarea {...field_11} autoFocus disabled={isPending} placeholder={t('health_problems_description')} className="h-32" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>} />}
              </div>

              {/* Travel Restrictions column */}
              <div className="space-y-3">
                <FormField control={form.control} name="travelRestrictions" render={({
                field: field_12
              }) => <FormItem className="flex flex-row items-center justify-between gap-4 rounded-lg border p-4">
                      <FormLabel required>{t('travel_restrictions')}</FormLabel>
                      <FormControl>
                        <Switch checked={field_12.value} onCheckedChange={field_12.onChange} disabled={isPending} />
                      </FormControl>
                    </FormItem>} />
                {travelRestrictions && <FormField control={form.control} name="travelRestrictionsDescription" render={({
                field: field_13
              }) => <FormItem>
                        <FormLabel required>{t('travel_restrictions_description')}</FormLabel>
                        <FormControl>
                          <Textarea {...field_13} autoFocus disabled={isPending} placeholder={t('travel_restrictions_description')} className="h-32" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>} />}
              </div>
            </div>
          </div>

          <div className="bg-card space-y-6 rounded-lg border p-6 shadow-xs">
            <h3 className="text-xl font-semibold">{t('education_history')}</h3>
            <div className="space-y-6">
              {educationFields.fields.map((item_6, index_6) => <div key={item_6.id} className="space-y-4 rounded-lg border p-4">
                  <div className="flex items-center justify-between gap-4">
                    <h4 className="font-medium">{t('education_history')}</h4>
                    <Button type="button" variant="destructive" onClick={() => educationFields.remove(index_6)} disabled={isPending}>
                      <Trash2Icon />
                      {t('remove')}
                    </Button>
                  </div>
                  <div className="grid items-start gap-4 md:grid-cols-2 lg:grid-cols-3">
                    <FormField control={form.control} name={`education.${index_6}.level`} render={({
                  field: field_14
                }) => <FormItem>
                          <FormLabel required>{t('education_level')}</FormLabel>
                          <Select value={field_14.value ?? ''} onValueChange={field_14.onChange} disabled={isPending}>
                            <FormControl>
                              <SelectTrigger className="w-full">
                                <SelectValue placeholder={t('select')} />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent position="popper">
                              {educationLevelOptions.map(option_1 => <SelectItem key={option_1.value} value={option_1.value}>
                                  {option_1.label}
                                </SelectItem>)}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>} />
                    <FormField control={form.control} name={`education.${index_6}.schoolName`} render={({
                  field: field_15
                }) => <FormItem>
                          <FormLabel required>{t('school_name')}</FormLabel>
                          <FormControl>
                            <Input {...field_15} disabled={isPending} placeholder={t('school_name')} className="h-9" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>} />
                    <FormField control={form.control} name={`education.${index_6}.department`} render={({
                  field: field_16
                }) => <FormItem>
                          <FormLabel>{t('department')}</FormLabel>
                          <FormControl>
                            <Input {...field_16} disabled={isPending} placeholder={t('department')} className="h-9" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>} />
                    <FormField control={form.control} name={`education.${index_6}.graduationStatus`} render={({
                  field: field_17
                }) => <FormItem>
                          <FormLabel required>{t('graduation_status')}</FormLabel>
                          <Select value={field_17.value ?? ''} onValueChange={field_17.onChange} disabled={isPending}>
                            <FormControl>
                              <SelectTrigger className="w-full">
                                <SelectValue placeholder={t('select')} />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent position="popper">
                              {graduationStatusOptions.map(option_2 => <SelectItem key={option_2.value} value={option_2.value}>
                                  {option_2.label}
                                </SelectItem>)}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>} />
                    <FormField control={form.control} name={`education.${index_6}.startDate`} render={({
                  field: field_18
                }) => <FormItem>
                          <FormLabel required>{t('start_date')}</FormLabel>
                          <FormControl>
                            <MonthPicker locale={locale} value={field_18.value} onChange={field_18.onChange} placeholder={t('start_date')} isInvalid={!!form.formState.errors.workExperience?.[index_6]?.startDate} disabled={isPending} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>} />
                    <FormField control={form.control} name={`education.${index_6}.endDate`} render={({
                  field: field_19
                }) => <FormItem>
                          <FormLabel>{t('end_date')}</FormLabel>
                          <FormControl>
                            <MonthPicker locale={locale} value={field_19.value} onChange={field_19.onChange} placeholder={t('end_date')} isInvalid={!!form.formState.errors.education?.[index_6]?.endDate} disabled={isPending} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>} />
                  </div>
                </div>)}
              <Button type="button" variant="outline" onClick={() => educationFields.append(createEducationItem())} disabled={isPending}>
                <PlusIcon />
                {t('add_education')}
              </Button>
            </div>
          </div>

          <div className="bg-card space-y-6 rounded-lg border p-6 shadow-xs">
            <h3 className="text-xl font-semibold">{t('foreign_languages')}</h3>
            <div className="space-y-6">
              {languageFields.fields.map((item_7, index_7) => <div key={item_7.id} className="space-y-4 rounded-lg border p-4">
                  <div className="flex justify-end">
                    <Button type="button" variant="destructive" onClick={() => languageFields.remove(index_7)} disabled={isPending}>
                      <Trash2Icon />
                      {t('remove')}
                    </Button>
                  </div>
                  <div className="grid items-start gap-4 md:grid-cols-2 lg:grid-cols-4">
                    <FormField control={form.control} name={`foreignLanguages.${index_7}.language`} render={({
                  field: field_20
                }) => <FormItem className="lg:col-span-4">
                          <FormLabel required>{t('language_name')}</FormLabel>
                          <FormControl>
                            <Input {...field_20} disabled={isPending} placeholder={t('language_name')} className="h-9" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>} />
                    {(['listening', 'speaking', 'reading', 'writing'] as const).map(name => <FormField key={name} control={form.control} name={`foreignLanguages.${index_7}.${name}`} render={({
                  field: field_21
                }) => <FormItem>
                            <FormLabel required>{t(name)}</FormLabel>
                            <Select value={field_21.value ?? ''} onValueChange={field_21.onChange} disabled={isPending}>
                              <FormControl>
                                <SelectTrigger className="w-full">
                                  <SelectValue placeholder={t('select')} />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent position="popper">
                                {languageProficiencyOptions.map(option_3 => <SelectItem key={option_3.value} value={option_3.value}>
                                    {option_3.label}
                                  </SelectItem>)}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>} />)}
                  </div>
                </div>)}
              <Button type="button" variant="outline" onClick={() => languageFields.append({
              language: '',
              listening: undefined,
              speaking: undefined,
              reading: undefined,
              writing: undefined
            })} disabled={isPending}>
                <PlusIcon />
                {t('add_language')}
              </Button>
            </div>
          </div>

          <div className="bg-card space-y-6 rounded-lg border p-6 shadow-xs">
            <h3 className="text-xl font-semibold">{t('work_experience')}</h3>
            <div className="space-y-6">
              {workExperienceFields.fields.map((item_8, index_8) => {
              const currentlyWorking = form.watch(`workExperience.${index_8}.currentlyWorking`);
              return <div key={item_8.id} className="space-y-4 rounded-lg border p-4">
                    <div className="flex justify-end">
                      <Button type="button" variant="destructive" onClick={() => workExperienceFields.remove(index_8)} disabled={isPending}>
                        <Trash2Icon />
                        {t('remove')}
                      </Button>
                    </div>
                    <div className="grid items-start gap-4 md:grid-cols-2">
                      <FormField control={form.control} name={`workExperience.${index_8}.companyName`} render={({
                    field: field_22
                  }) => <FormItem>
                            <FormLabel required>{t('company_name')}</FormLabel>
                            <FormControl>
                              <Input {...field_22} disabled={isPending} placeholder={t('company_name')} className="h-9" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>} />
                      <FormField control={form.control} name={`workExperience.${index_8}.position`} render={({
                    field: field_23
                  }) => <FormItem>
                            <FormLabel required>{t('position_title')}</FormLabel>
                            <FormControl>
                              <Input {...field_23} disabled={isPending} placeholder={t('position_title')} className="h-9" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>} />
                      <FormField control={form.control} name={`workExperience.${index_8}.startDate`} render={({
                    field: field_24
                  }) => <FormItem>
                            <FormLabel required>{t('start_date')}</FormLabel>
                            <FormControl>
                              <MonthPicker locale={locale} value={field_24.value} onChange={field_24.onChange} placeholder={t('start_date')} isInvalid={!!form.formState.errors.education?.[index_8]?.startDate} disabled={isPending} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>} />
                      <FormField control={form.control} name={`workExperience.${index_8}.endDate`} render={({
                    field: field_25
                  }) => <FormItem>
                            <FormLabel required={!currentlyWorking}>{t('end_date')}</FormLabel>
                            <FormControl>
                              <MonthPicker locale={locale} value={field_25.value} onChange={field_25.onChange} placeholder={t('end_date')} isInvalid={!!form.formState.errors.workExperience?.[index_8]?.endDate} disabled={isPending || !!currentlyWorking} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>} />
                      <FormField control={form.control} name={`workExperience.${index_8}.currentlyWorking`} render={({
                    field: field_26
                  }) => <FormItem className="flex flex-row items-center gap-3 rounded-lg border p-4 md:col-span-2">
                            <Checkbox id={`work-experience-${index_8}-current`} checked={field_26.value} onCheckedChange={checked => {
                      const nextValue = checked === true;
                      field_26.onChange(nextValue);
                      if (nextValue) {
                        form.setValue(`workExperience.${index_8}.endDate`, '', {
                          shouldValidate: true
                        });
                      }
                    }} disabled={isPending} />
                            <FormLabel className="cursor-pointer" htmlFor={`work-experience-${index_8}-current`}>
                              {t('currently_working')}
                            </FormLabel>
                            <FormMessage />
                          </FormItem>} />
                      <FormField control={form.control} name={`workExperience.${index_8}.description`} render={({
                    field: field_27
                  }) => <FormItem className="md:col-span-2">
                            <FormLabel>{t('job_description')}</FormLabel>
                            <FormControl>
                              <Textarea {...field_27} disabled={isPending} placeholder={t('job_description')} className="h-32" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>} />
                    </div>
                  </div>;
            })}
              <Button type="button" variant="outline" onClick={() => workExperienceFields.append({
              companyName: '',
              position: '',
              startDate: '',
              endDate: '',
              description: '',
              currentlyWorking: false
            })} disabled={isPending}>
                <PlusIcon />
                {t('add_work_experience')}
              </Button>
            </div>
          </div>

          <div className="bg-card space-y-6 rounded-lg border p-6 shadow-xs">
            <h3 className="text-xl font-semibold">{t('skills')}</h3>
            <div className="space-y-6">
              {skillFields.fields.map((item_9, index_9) => <div key={item_9.id} className="grid gap-4 rounded-lg border p-4 md:grid-cols-[1fr_220px_auto] md:items-start">
                  <FormField control={form.control} name={`skills.${index_9}.skillName`} render={({
                field: field_28
              }) => <FormItem>
                        <FormLabel required>{t('skill_name')}</FormLabel>
                        <FormControl>
                          <Input {...field_28} disabled={isPending} placeholder={t('skill_name')} className="h-9" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>} />
                  <FormField control={form.control} name={`skills.${index_9}.level`} render={({
                field: field_29
              }) => <FormItem>
                        <FormLabel required>{t('skill_level')}</FormLabel>
                        <Select value={field_29.value ?? ''} onValueChange={field_29.onChange} disabled={isPending}>
                          <FormControl>
                            <SelectTrigger className="w-full">
                              <SelectValue placeholder={t('select')} />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent position="popper">
                            {skillLevelOptions.map(option_4 => <SelectItem key={option_4.value} value={option_4.value}>
                                {option_4.label}
                              </SelectItem>)}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>} />
                  <div className="pt-0 md:pt-7">
                    <Button type="button" variant="destructive" onClick={() => skillFields.remove(index_9)} disabled={isPending}>
                      <Trash2Icon />
                      {t('remove')}
                    </Button>
                  </div>
                </div>)}
              <Button type="button" variant="outline" onClick={() => skillFields.append({
              skillName: '',
              level: undefined
            })} disabled={isPending}>
                <PlusIcon />
                {t('add_skill')}
              </Button>
            </div>
          </div>

          <div className="bg-card space-y-6 rounded-lg border p-6 shadow-xs">
            <h3 className="text-xl font-semibold">{t('certifications')}</h3>
            <div className="space-y-6">
              {certificationFields.fields.map((item_10, index_10) => <div key={item_10.id} className="space-y-4 rounded-lg border p-4">
                  <div className="flex justify-end">
                    <Button type="button" variant="destructive" onClick={() => certificationFields.remove(index_10)} disabled={isPending}>
                      <Trash2Icon />
                      {t('remove')}
                    </Button>
                  </div>
                  <div className="grid items-start gap-4 md:grid-cols-2 lg:grid-cols-4">
                    <FormField control={form.control} name={`certifications.${index_10}.certificationName`} render={({
                  field: field_30
                }) => <FormItem>
                          <FormLabel required>{t('certification_name')}</FormLabel>
                          <FormControl>
                            <Input {...field_30} disabled={isPending} placeholder={t('certification_name')} className="h-9" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>} />
                    <FormField control={form.control} name={`certifications.${index_10}.issuingOrganization`} render={({
                  field: field_31
                }) => <FormItem>
                          <FormLabel required>{t('issuing_organization')}</FormLabel>
                          <FormControl>
                            <Input {...field_31} disabled={isPending} placeholder={t('issuing_organization')} className="h-9" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>} />
                    <FormField control={form.control} name={`certifications.${index_10}.certificationDate`} render={({
                  field: field_32
                }) => <FormItem>
                          <FormLabel>{t('certification_date')}</FormLabel>
                          <FormControl>
                            <MonthPicker locale={locale} value={field_32.value} onChange={field_32.onChange} placeholder={t('certification_date')} isInvalid={!!form.formState.errors.certifications?.[index_10]?.certificationDate} disabled={isPending} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>} />
                    <FormField control={form.control} name={`certifications.${index_10}.validityPeriod`} render={({
                  field: field_33
                }) => <FormItem>
                          <FormLabel>{t('validity_period')}</FormLabel>
                          <FormControl>
                            <Input {...field_33} disabled={isPending} placeholder={t('validity_period')} className="h-9" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>} />
                  </div>
                </div>)}
              <Button type="button" variant="outline" onClick={() => certificationFields.append({
              certificationName: '',
              issuingOrganization: '',
              certificationDate: '',
              validityPeriod: ''
            })} disabled={isPending}>
                <PlusIcon />
                {t('add_certification')}
              </Button>
            </div>
          </div>

          <div className="bg-card space-y-6 rounded-lg border p-6 shadow-xs">
            <h3 className="text-xl font-semibold">{t('references')}</h3>
            <div className="space-y-6">
              {referenceFields.fields.map((item_11, index_11) => <div key={item_11.id} className="space-y-4 rounded-lg border p-4">
                  <div className="flex justify-end">
                    <Button type="button" variant="destructive" onClick={() => referenceFields.remove(index_11)} disabled={isPending}>
                      <Trash2Icon />
                      {t('remove')}
                    </Button>
                  </div>
                  <div className="grid items-start gap-4 md:grid-cols-2 lg:grid-cols-4">
                    <FormField control={form.control} name={`references.${index_11}.name`} render={({
                  field: field_34
                }) => <FormItem>
                          <FormLabel required>{t('name')}</FormLabel>
                          <FormControl>
                            <Input {...field_34} disabled={isPending} placeholder={t('name')} className="h-9" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>} />
                    <FormField control={form.control} name={`references.${index_11}.position`} render={({
                  field: field_35
                }) => <FormItem>
                          <FormLabel>{t('position_title')}</FormLabel>
                          <FormControl>
                            <Input {...field_35} disabled={isPending} placeholder={t('position_title')} className="h-9" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>} />
                    <FormField control={form.control} name={`references.${index_11}.phone`} render={({
                  field: field_36
                }) => <FormItem>
                          <FormLabel>{t('phone')}</FormLabel>
                          <FormControl>
                            <PhoneInput international value={field_36.value} onChange={field_36.onChange} disabled={isPending} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>} />
                    <FormField control={form.control} name={`references.${index_11}.email`} render={({
                  field: field_37
                }) => <FormItem>
                          <FormLabel>{t('email')}</FormLabel>
                          <FormControl>
                            <Input {...field_37} type="email" disabled={isPending} placeholder={t('email')} className="h-9" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>} />
                  </div>
                </div>)}
              <Button type="button" variant="outline" onClick={() => referenceFields.append({
              name: '',
              position: '',
              phone: '',
              email: ''
            })} disabled={isPending}>
                <PlusIcon />
                {t('add_reference')}
              </Button>
            </div>
          </div>

          <div className="bg-card space-y-6 rounded-lg border p-6 shadow-xs">
            <h3 className="text-xl font-semibold">{t('documents_other')}</h3>
            <div className="grid items-start gap-6 md:grid-cols-2 lg:grid-cols-3">
              <FormField control={form.control} name="photo" render={() => <FormItem>
                    <FormLabel htmlFor="job-application-photo">{t('photo')}</FormLabel>
                    <input id="job-application-photo" ref={photoInputRef} type="file" accept="image/*" disabled={isPending} className="hidden" onChange={event => {
                const file_0 = event.target.files?.[0];
                form.setValue('photo', file_0, {
                  shouldValidate: true
                });
              }} />
                    <FormControl>
                      <Button type="button" variant="outline" className="h-9 w-full justify-start gap-2" onClick={() => photoInputRef.current?.click()} disabled={isPending}>
                        <UploadIcon />
                        {t('photo')}
                      </Button>
                    </FormControl>
                    {selectedPhoto && <div className="flex items-center justify-between gap-3 rounded-lg border p-3">
                        <div className="flex min-w-0 items-center gap-2">
                          <FileIcon className="text-muted-foreground h-4 w-4 shrink-0" />
                          <span className="truncate text-sm">{selectedPhoto.name}</span>
                        </div>
                        <Button type="button" variant="ghost" size="icon-sm" onClick={() => {
                  form.setValue('photo', undefined, {
                    shouldValidate: true
                  });
                  if (photoInputRef.current) {
                    photoInputRef.current.value = '';
                  }
                }} disabled={isPending}>
                          <XIcon />
                        </Button>
                      </div>}
                    <FormMessage />
                  </FormItem>} />
              <FormField control={form.control} name="files" render={() => <FormItem>
                    <FormLabel htmlFor="job-application-files">{t('attachments')}</FormLabel>
                    <input id="job-application-files" ref={filesInputRef} type="file" multiple disabled={isPending} className="hidden" onChange={event_0 => {
                const files = Array.from(event_0.target.files ?? []);
                if (files.length > 0) {
                  const merged = [...selectedFiles, ...files].slice(0, 5);
                  form.setValue('files', merged, {
                    shouldValidate: true
                  });
                }
                event_0.target.value = '';
              }} />
                    <div className="space-y-3">
                      <FormControl>
                        <Button type="button" variant="outline" className="h-9 w-full justify-start gap-2" onClick={() => filesInputRef.current?.click()} disabled={isPending || selectedFiles.length >= 5}>
                          <PlusIcon />
                          {t('add_file')}
                        </Button>
                      </FormControl>
                      {selectedFiles.length > 0 && <div className="space-y-2">
                          {selectedFiles.map((file_1, idx) => <div key={`${file_1.name}-${idx}`} className="flex items-center justify-between gap-3 rounded-lg border p-3">
                              <div className="flex min-w-0 items-center gap-2">
                                <FileIcon className="text-muted-foreground h-4 w-4 shrink-0" />
                                <span className="truncate text-sm">{file_1.name}</span>
                              </div>
                              <Button type="button" variant="ghost" size="icon-sm" onClick={() => {
                      form.setValue('files', selectedFiles.filter((_, fileIndex) => fileIndex !== idx), {
                        shouldValidate: true
                      });
                      if (filesInputRef.current) {
                        filesInputRef.current.value = '';
                      }
                    }} disabled={isPending}>
                                <XIcon />
                              </Button>
                            </div>)}
                        </div>}
                    </div>
                    <FormMessage />
                  </FormItem>} />
              <FormField control={form.control} name="position" render={({
              field: field_38
            }) => <FormItem>
                    <FormLabel>{t('applied_position')}</FormLabel>
                    <FormControl>
                      <Input {...field_38} disabled={isPending} placeholder={t('applied_position')} className="h-9" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
              <FormField control={form.control} name="availableFrom" render={({
              field: field_39
            }) => <FormItem>
                    <FormLabel>{t('available_from')}</FormLabel>
                    <FormControl>
                      <DatePicker locale={locale} value={field_39.value ? new Date(`${field_39.value}T12:00:00`) : undefined} onChange={date_0 => field_39.onChange(date_0 ? format(date_0, 'yyyy-MM-dd') : '')} placeholder={t('available_from')} isInvalid={!!form.formState.errors.availableFrom} disabled={isPending} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
              <FormField control={form.control} name="additionalInformation" render={({
              field: field_40
            }) => <FormItem className="md:col-span-2 lg:col-span-3">
                    <FormLabel>{t('additional_information')}</FormLabel>
                    <FormControl>
                      <Textarea {...field_40} disabled={isPending} placeholder={t('additional_information')} className="h-32" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
            </div>
          </div>

          {captcha && captchaData && <div className="bg-card space-y-6 rounded-lg border p-6 shadow-xs">
              <h3 className="text-xl font-semibold">{t('security_code')}</h3>
              <FormField control={form.control} name="captchaText" render={({
            field: field_41
          }) => <FormItem>
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:gap-4">
                      <div className="flex items-center gap-1 sm:mt-5">
                        <div className="bg-muted relative overflow-hidden rounded-lg border">
                          {isLoadingCaptcha ? <div className="flex h-15 w-45 items-center justify-center">
                              <RefreshCw className="text-muted-foreground h-6 w-6 animate-spin" />
                            </div> : <Image src={captchaData.image} alt={t('security_code')} width={180} height={60} className="h-auto w-auto" unoptimized />}
                        </div>
                        <Button type="button" variant="outline" size="icon-lg" tabIndex={-1} onClick={async () => {
                  field_41.onChange('');
                  await refreshCaptcha();
                }} disabled={isPending || isLoadingCaptcha} className="h-11.5 w-11.5 shrink-0">
                          <RefreshCw />
                        </Button>
                      </div>
                      <div className="w-full space-y-2 sm:w-48">
                        <FormLabel required>{t('security_code')}</FormLabel>
                        <FormControl>
                          <Input {...field_41} disabled={isPending} placeholder={t('enter_security_code')} autoComplete="off" className="h-9 px-3 py-5" />
                        </FormControl>
                        <FormMessage />
                      </div>
                    </div>
                  </FormItem>} />
            </div>}

          <Button type="submit" variant="brand" loading={isPending} disabled={isPending || captcha && !captchaData} className="h-12 w-full text-base font-semibold">
            {!isPending && <SendIcon />}
            {isPending ? t('submitting') : t('submit_application')}
          </Button>
        </form>
      </Form>
    </div>;
};
export default JobApplicationForm;